import { i18n } from "shared/i18n";

/**
 * A3 -- one catalogue for the governance DECISION vocabularies.
 *
 * The sign-off panel printed our column values at a business reader:
 * `ibs_supporting`, `tier_0`, `core_mvb`, `ces`, `deferred`. The
 * round-2 catalogue (workflowState.ts) covered workflow states and job
 * roles; these are the decisions themselves, and they are what the
 * signature is about.
 *
 * Keyed by the LINE, not by the value alone, because the vocabularies
 * collide: `out_of_scope` is a service classification ("outside
 * resilience scope") AND an MVB tier ("outside the minimum viable
 * bank"). A flat lookup has to pick one and is then wrong half the
 * time, for precisely the reader this exists to help.
 *
 * Explicit rather than a snake_case transform, for the same reason as
 * the sibling: "tier_0" transforms to "Tier 0" and still says nothing.
 * Anything uncatalogued falls back to the de-underscored form, so a
 * new enum degrades to something readable rather than vanishing.
 *
 * Only values the backend MARKS as terms come through here. A
 * rationale or a service name must reach the reader as it was written.
 */

/** Admission proposal + service classification -- the "what is being
 *  decided" line. Disjoint vocabularies that share one heading. */
const PROPOSAL: readonly string[] = [
  "included",
  "deferred",
  "excluded",
  "ibs_supporting",
  "supporting",
  "non_critical",
  "out_of_scope",
];

/** Criticality tier (runbook 7.3) and MVB tier share this heading and
 *  do not overlap. The tier number alone means nothing to a reader who
 *  does not have the runbook open. */
const TIER: readonly string[] = [
  "tier_0",
  "tier_1",
  "tier_2",
  "tier_3",
  "core_mvb",
  "mvb",
  "watchlist",
  "out_of_scope",
];

/** Runbook stage 3. `ces` and `ids` are the methodology's own
 *  abbreviations and are unreadable on sight. */
const NEXT_SIGNER: readonly string[] = ["cio", "business_owner"];

// Round 16 N4 -- Route 2 read "Pathway | Cif": the key was missing.
const PATHWAY: readonly string[] = ["ces", "ids", "standard", "sms", "cif"];

const RECOMMENDATION: readonly string[] = [
  "include",
  "defer",
  "exclude",
  "core_mvb",
  "mvb",
  "watchlist",
  "out_of_scope",
];

/* Round 11 F1 -- the SAMA outcome categories.
 *
 * Not a classification, though it was arriving on the classification
 * line: this is which customer outcome a disruption would damage. It
 * missed the PROPOSAL vocabulary, fell through to the de-underscored
 * column, and put "Minimize customer harm" on the owner's attestation
 * panel three clicks from a register reading "minimise". */
const OUTCOME: readonly string[] = [
  "minimize_customer_harm",
  "retain_customer_confidence",
  "regulatory_engagement",
  "protect_safety_and_soundness",
];

// B1 -- "puts in force: nothing". An empty MVB set is a real answer
// and the alarming one; a missing line reads as no information.
const MEMBERS: readonly string[] = ["empty_set"];

/* Round 9 G -- the line -> (group, closed vocabulary) map. The words
   are in the catalogue; the GROUP is what tells two colliding
   vocabularies apart, which is the whole reason this file is keyed by
   line rather than by value. */
const BY_LINE: Record<string, [string, readonly string[]]> = {
  "myWork.summary.members": ["members", MEMBERS],
  "myWork.summary.proposal": ["proposal", PROPOSAL],
  "myWork.summary.classification": ["proposal", PROPOSAL],
  "myWork.summary.tier": ["tier", TIER],
  "myWork.summary.pathway": ["pathway", PATHWAY],
  "myWork.summary.recommendation": ["recommendation", RECOMMENDATION],
  "myWork.summary.outcome": ["outcome", OUTCOME],
  // Round 16 N2 -- who signs after this signature, on Route 2.
  "myWork.summary.nextSigner": ["nextSigner", NEXT_SIGNER],
};

function fromCatalogue(
  group: string,
  key: string,
  known: readonly string[],
): string | null {
  if (!known.includes(key)) return null;
  const out = i18n.t(`vocab.governance.${group}.${key}`);
  // A key the catalogue has not got back returns the key itself.
  return out.startsWith("vocab.") ? null : out;
}

function humanise(raw: string): string {
  const spaced = raw.replace(/_/g, " ").trim();
  if (!spaced) return raw;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * A governance decision value as a reader should see it.
 *
 * `labelKey` names the line the value sits on, which is what tells two
 * colliding vocabularies apart. Omitting it falls back to a lookup
 * across all of them, which is right whenever the value is unambiguous
 * and no worse than the raw enum when it is not.
 */
export function termLabel(
  raw: string | null | undefined,
  labelKey?: string | null,
): string {
  if (!raw) return "";
  const key = raw.trim().toLowerCase();
  const scoped = labelKey ? BY_LINE[labelKey] : undefined;
  if (scoped) {
    const [group, known] = scoped;
    return fromCatalogue(group, key, known) ?? humanise(raw);
  }
  for (const [group, known] of [
    ["proposal", PROPOSAL],
    ["tier", TIER],
    ["pathway", PATHWAY],
    ["recommendation", RECOMMENDATION],
    ["outcome", OUTCOME],
  ] as [string, readonly string[]][]) {
    const out = fromCatalogue(group, key, known);
    if (out) return out;
  }
  return humanise(raw);
}

export const CATALOGUED_LINES = Object.keys(BY_LINE);
